import React from 'react';
import {
  Card,
  Switch,
  Typography,
  Space,
  Divider,
  Alert,
  Spin,
  message,
  Button,
} from 'antd';
import {
  BellOutlined,
  MailOutlined,
  MobileOutlined,
  NotificationOutlined,
  SaveOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useNotificationPreferences } from '../../hooks/useNotifications';
import './notification-preferences.css';

const { Title, Text, Paragraph } = Typography;

/**
 * Preference Item - Individual preference toggle
 */
const PreferenceItem = ({
  icon,
  label,
  description,
  checked,
  onChange,
  disabled,
  loading,
}) => {
  return (
    <div className={`preference-item ${disabled ? 'disabled' : ''}`}>
      <div className="preference-info">
        <Space>
          <span className="preference-icon">{icon}</span>
          <div className="preference-text">
            <Text strong>{label}</Text>
            <Paragraph type="secondary" style={{ margin: 0, fontSize: '12px' }}>
              {description}
            </Paragraph>
          </div>
        </Space>
      </div>
      <Switch
        checked={checked}
        onChange={onChange}
        disabled={disabled || loading}
        size="default"
      />
    </div>
  );
};

/**
 * AdminNotificationPreferences - Notification settings component
 * Allows admins to manage their notification preferences
 */
const AdminNotificationPreferences = () => {
  const {
    preferences,
    loading,
    saving,
    updatePreference,
    updatePreferences,
    refresh,
  } = useNotificationPreferences();

  /**
   * Handle preference toggle
   */
  const handleToggle = async (key, value) => {
    try {
      await updatePreference(key, value);
    } catch (error) {
      console.error('Failed to update preference:', error);
      message.error('Failed to update preference');
    }
  };

  /**
   * Handle save all preferences
   */
  const handleSaveAll = async () => {
    try {
      await updatePreferences(preferences);
      message.success('All preferences saved successfully');
    } catch (error) {
      console.error('Failed to save preferences:', error);
      message.error('Failed to save preferences');
    }
  };

  return (
    <div className="notification-preferences">
      <Spin spinning={loading}>
        <Card>
          {/* Header */}
          <div className="preferences-header">
            <div>
              <Title level={3} style={{ margin: 0 }}>
                <NotificationOutlined style={{ marginRight: '8px' }} />
                Notification Preferences
              </Title>
              <Text type="secondary">
                Manage how you receive notifications
              </Text>
            </div>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={refresh}
                loading={loading}
              >
                Refresh
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSaveAll}
                loading={saving}
              >
                Save All
              </Button>
            </Space>
          </div>

          <Divider />

          {/* Info Alert */}
          <Alert
            message="Notification Preferences"
            description="These settings control how you receive notifications. Changes take effect immediately."
            type="info"
            showIcon
            style={{ marginBottom: '24px' }}
          />

          {/* Delivery Channels Section */}
          <div className="preference-section">
            <Title level={4} style={{ marginBottom: '16px' }}>
              Delivery Channels
            </Title>
            <Paragraph type="secondary">
              Choose how you want to receive notifications
            </Paragraph>

            <div className="preference-list">
              <PreferenceItem
                icon={<BellOutlined />}
                label="Push Notifications"
                description="Receive real-time push notifications on your device"
                checked={preferences.push_enabled}
                onChange={(checked) => handleToggle('push_enabled', checked)}
                loading={saving}
              />

              <PreferenceItem
                icon={<MailOutlined />}
                label="Email Notifications"
                description="Receive notifications via email"
                checked={preferences.email_enabled}
                onChange={(checked) => handleToggle('email_enabled', checked)}
                loading={saving}
              />

              <PreferenceItem
                icon={<MobileOutlined />}
                label="SMS Notifications"
                description="Receive notifications via text message"
                checked={preferences.sms_enabled}
                onChange={(checked) => handleToggle('sms_enabled', checked)}
                loading={saving}
              />
            </div>
          </div>

          <Divider />

          {/* Notification Types Section */}
          <div className="preference-section">
            <Title level={4} style={{ marginBottom: '16px' }}>
              Notification Types
            </Title>
            <Paragraph type="secondary">
              Choose which types of notifications you want to receive
            </Paragraph>

            <div className="preference-list">
              <PreferenceItem
                icon={<NotificationOutlined />}
                label="Marketing Notifications"
                description="Receive promotional and marketing communications"
                checked={preferences.marketing_enabled || false}
                onChange={(checked) => handleToggle('marketing_enabled', checked)}
                loading={saving}
              />
            </div>
          </div>

          <Divider />

          {/* Tips Section */}
          <div className="preference-tips">
            <Title level={5}>Tips</Title>
            <ul>
              <li>
                <Text>Enable push notifications for real-time updates on appointments and claims</Text>
              </li>
              <li>
                <Text>Email notifications provide a backup if you miss push notifications</Text>
              </li>
              <li>
                <Text>SMS notifications are useful for urgent alerts when you're offline</Text>
              </li>
              <li>
                <Text>Marketing notifications can be disabled at any time</Text>
              </li>
            </ul>
          </div>
        </Card>
      </Spin>
    </div>
  );
};

export default AdminNotificationPreferences;



